import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Container, LinearProgress, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { AppHelper } from "../../AppHelper";
import AppTopBar from "../common/app-top-bar";
import GameIcon from "./game-icon";
import { GAME_GRADES, GAME_GROUPS, GAME_SKILLS, isGameSkill, skillLabel } from "../../constants/game-skills";

const ROUND_SIZE = 5;
// The built-in word bank only covers these; other games need the server.
const FALLBACK_SKILLS = ["mix", "vocabulary", "spelling"];

// prompt uses ____ for the missing word. kind: 'vocabulary' | 'spelling'. Content is AI-drafted; have a teacher review it.
const WORDS = {
  P4: [
    { kind: "vocabulary", prompt: "The soup was so ____ that I asked for a second bowl.", answer: "delicious", wrong: ["hungry", "noisy", "narrow"] },
    { kind: "vocabulary", prompt: "\"I am scared,\" Mei ____ so that no one could hear.", answer: "whispered", wrong: ["shouted", "laughed", "sang"] },
    { kind: "vocabulary", prompt: "Dad ____ up the stairs after a long day at work.", answer: "trudged", wrong: ["floated", "melted", "sparkled"] },
    { kind: "vocabulary", prompt: "The ice cream melted because the day was ____.", answer: "scorching", wrong: ["freezing", "gloomy", "silent"] },
    { kind: "vocabulary", prompt: "Ali ____ at the clock and saw that he was late.", answer: "glanced", wrong: ["grabbed", "galloped", "gathered"] },
    { kind: "vocabulary", prompt: "The puppy was ____ when its owner came home.", answer: "delighted", wrong: ["frightened", "exhausted", "puzzled"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "wonderful", wrong: ["wonderfull", "wunderful", "wonderfal"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "careful", wrong: ["carefull", "carful", "carefal"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "library", wrong: ["libary", "liberry", "librery"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "mathematics", wrong: ["mathamatics", "mathmatics", "matematics"] },
  ],
  P5: [
    { kind: "vocabulary", prompt: "Mum was ____ with my results.", answer: "delighted", wrong: ["delicate", "defeated", "deserted"] },
    { kind: "vocabulary", prompt: "The hikers were ____ after climbing for six hours.", answer: "exhausted", wrong: ["refreshed", "cheerful", "generous"] },
    { kind: "vocabulary", prompt: "The thief ____ down the alley when he heard the siren.", answer: "sprinted", wrong: ["strolled", "crawled", "wandered"] },
    { kind: "vocabulary", prompt: "Mr Lim was ____ when he saw the mess in the classroom.", answer: "furious", wrong: ["curious", "generous", "joyful"] },
    { kind: "vocabulary", prompt: "\"Stop!\" the guard ____.", answer: "shouted", wrong: ["whispered", "hummed", "mumbled"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "embarrassing", wrong: ["embarassing", "embarrasing", "embaressing"] },
    { kind: "spelling", prompt: "I bought some ____ for school. (pens, paper, rulers)", answer: "stationery", wrong: ["stationary", "stationry", "stasionery"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "performance", wrong: ["performence", "perfomance", "performanse"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "announced", wrong: ["anounced", "annonced", "annouced"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "received", wrong: ["recieved", "receeved", "reseived"] },
  ],
};

const CONFETTI_COLORS = ["#FFC63D", "#FF6B5E", "#8A6BFF", "#19B5A5", "#25204A"];

function Confetti() {
  const pieces = useMemo(
    () => Array.from({ length: 36 }, (_, i) => ({
      left: Math.random() * 100, delay: Math.random() * 0.8, color: CONFETTI_COLORS[i % CONFETTI_COLORS.length], rot: Math.random() * 360,
    })),
    []
  );
  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <i key={i} style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, backgroundColor: p.color, transform: `rotate(${p.rot}deg)` }} />
      ))}
    </div>
  );
}

function shuffle(list) {
  const a = list.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Lenient comparison for typed answers: ignores case, extra spaces, curly quotes and spacing before punctuation.
function normaliseText(s) {
  return String(s)
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+([.,!?;:])/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}
function sameText(a, b) {
  return normaliseText(a) === normaliseText(b);
}

function buildRound(grade, skill) {
  const bank = WORDS[grade] || WORDS.P5;
  const preferred = skill ? bank.filter((w) => w.kind === skill) : bank;
  const picked = shuffle(preferred.length >= ROUND_SIZE ? preferred : bank).slice(0, ROUND_SIZE);
  return picked.map((w) => ({ format: "choice", prompt: w.prompt, answer: w.answer, options: shuffle([w.answer, ...w.wrong]) }));
}

// Tap the jumbled words in the right order to build the sentence.
function UnscrambleQuestion({ q, hintSignal, onFinish, disabled }) {
  const [chosen, setChosen] = useState([]);
  const prevHint = useRef(hintSignal);
  useEffect(() => {
    if (hintSignal === prevHint.current) return;
    prevHint.current = hintSignal;
    const first = q.first || q.answer.split(/\s+/)[0];
    const at = q.words.findIndex((w) => w === first);
    if (at >= 0) setChosen([at]);
  }, [hintSignal]);

  const add = (i) => !disabled && !chosen.includes(i) && setChosen((c) => [...c, i]);
  const remove = (i) => !disabled && setChosen((c) => c.filter((x) => x !== i));
  const built = chosen.map((i) => q.words[i]).join(" ");

  return (
    <>
      <Paper variant="outlined" sx={{ padding: 1.5, marginBottom: 2, minHeight: 64, backgroundColor: "#fff8e1", border: "2px dashed #25204A" }}>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {chosen.length === 0 && <Typography sx={{ color: "#888" }}>Tap the pieces below in the right order.</Typography>}
          {chosen.map((i) => (
            <Button key={i} variant="contained" color="secondary" onClick={() => remove(i)} disabled={disabled} sx={{ textTransform: "none", fontSize: "1.05rem" }}>
              {q.words[i]}
            </Button>
          ))}
        </Box>
      </Paper>
      <Box display="flex" flexWrap="wrap" gap={1}>
        {q.words.map((w, i) => (
          <Button key={i} variant="outlined" onClick={() => add(i)} disabled={disabled || chosen.includes(i)} sx={{ textTransform: "none", fontSize: "1.05rem" }}>
            {w}
          </Button>
        ))}
      </Box>
      {!disabled && (
        <Button variant="contained" color="success" sx={{ marginTop: 2 }} disabled={chosen.length !== q.words.length} onClick={() => onFinish(sameText(built, q.answer))}>
          Check
        </Button>
      )}
    </>
  );
}

// Tap a word, then tap its partner. Any wrong tap means the question is not counted as correct.
function MatchQuestion({ q, hintSignal, onFinish, disabled }) {
  const rights = useMemo(() => shuffle(q.pairs.map((p, i) => ({ text: p.right, i }))), [q]);
  const [matched, setMatched] = useState([]);
  const [selLeft, setSelLeft] = useState(null);
  const [wrongRight, setWrongRight] = useState(null);
  const mistakes = useRef(0);
  const prevHint = useRef(hintSignal);

  const matchedRef = useRef([]);
  const lock = (leftIdx) => {
    if (matchedRef.current.includes(leftIdx)) return;
    matchedRef.current = [...matchedRef.current, leftIdx];
    setMatched(matchedRef.current);
    setSelLeft(null);
    if (matchedRef.current.length === q.pairs.length) onFinish(mistakes.current === 0);
  };

  useEffect(() => {
    if (hintSignal === prevHint.current) return;
    prevHint.current = hintSignal;
    const open = q.pairs.findIndex((_, i) => !matchedRef.current.includes(i));
    if (open >= 0) lock(open);
  }, [hintSignal]);

  const pickRight = (r) => {
    if (disabled || selLeft === null || matched.includes(r.i)) return;
    if (r.i === selLeft) {
      lock(selLeft);
    } else {
      mistakes.current += 1;
      setWrongRight(r.i);
      setSelLeft(null);
      setTimeout(() => setWrongRight(null), 600);
    }
  };

  return (
    <Box display="flex" gap={2}>
      <Box display="flex" flexDirection="column" gap={1} flex={1}>
        {q.pairs.map((p, i) => (
          <Button
            key={i}
            variant={matched.includes(i) || selLeft === i ? "contained" : "outlined"}
            color={matched.includes(i) ? "success" : selLeft === i ? "secondary" : "primary"}
            disabled={disabled || matched.includes(i)}
            onClick={() => setSelLeft(i)}
            sx={{ textTransform: "none", fontSize: "1.05rem" }}
          >
            {p.left}
          </Button>
        ))}
      </Box>
      <Box display="flex" flexDirection="column" gap={1} flex={1}>
        {rights.map((r) => (
          <Button
            key={r.i}
            variant={matched.includes(r.i) || wrongRight === r.i ? "contained" : "outlined"}
            color={matched.includes(r.i) ? "success" : wrongRight === r.i ? "error" : "primary"}
            disabled={disabled || matched.includes(r.i)}
            onClick={() => pickRight(r)}
            sx={{ textTransform: "none", fontSize: "1.05rem" }}
          >
            {r.text}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

export default function PageWordSafari() {
  const navigate = useNavigate();
  const location = useLocation();
  const oUser = AppHelper.userCacheGet();
  const refTopBar = useRef(null);
  const params = new URLSearchParams(window.location.search);
  const initialGrade = params.get("grade") || (oUser && oUser.grade) || "P5";
  const [grade, setGrade] = useState(GAME_GRADES.includes(initialGrade) ? initialGrade : "P5");
  const initialSkill = isGameSkill(params.get("skill")) ? params.get("skill") : null;
  // null shows the Game Zone menu; "mix" mixes every category.
  const [skill, setSkill] = useState(initialSkill);
  const [loadError, setLoadError] = useState(false);
  const essayErrors = (location.state && location.state.errors) || [];
  const essayGrade = useRef(initialGrade);

  const [round, setRound] = useState([]);
  const [loading, setLoading] = useState(initialSkill !== null);
  const [roundKey, setRoundKey] = useState(0);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState("");
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [hintUsed, setHintUsed] = useState(false);
  const [hints, setHints] = useState(0);
  const [hintText, setHintText] = useState("");
  const [hiddenOptions, setHiddenOptions] = useState([]);
  const [hintSignal, setHintSignal] = useState(0);

  useEffect(() => {
    if (!oUser) navigate("/login");
  }, []);

  // New questions are fetched for every round so a learner does not keep seeing the same ones.
  // The built-in word bank is only a fallback if the server cannot supply any.
  useEffect(() => {
    if (!oUser || skill === null) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      let items = [];
      try {
        const res = await AppHelper.apiPost("api/practice/next", {
          token: oUser.token, grade, skill: skill === "mix" ? "" : skill, count: ROUND_SIZE,
          errors: roundKey === 0 && grade === essayGrade.current ? essayErrors : [],
        });
        if (res && res.status && Array.isArray(res.items)) items = res.items;
      } catch (e) {
        items = [];
      }
      if (items.length === 0 && FALLBACK_SKILLS.includes(skill)) items = buildRound(grade, skill === "mix" ? "" : skill);
      items = items.map((it) => (it.format === "choice" ? { ...it, options: shuffle(it.options) } : it));
      if (!cancelled) {
        setRound(items);
        setLoadError(items.length === 0);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roundKey, grade, skill]);

  if (!oUser) return null;

  const inGame = skill !== null;
  const finished = inGame && !loading && !loadError && index >= round.length;
  const q = round[index];
  const stars = correct >= round.length && round.length > 0 ? 3 : correct >= Math.ceil(round.length * 0.6) ? 2 : correct >= 1 ? 1 : 0;
  const answered = picked !== "" || result !== null;

  const onPick = (opt) => {
    if (answered) return;
    setPicked(opt);
    if (opt === q.answer) setCorrect((c) => c + 1);
  };
  const onFinish = (ok) => {
    if (answered) return;
    setResult(ok ? "right" : "wrong");
    if (ok) setCorrect((c) => c + 1);
  };
  const answerText = (item) => (item.format === "match" ? item.pairs.map((p) => `${p.left} = ${p.right}`).join(", ") : item.answer);
  const onCheck = () => {
    if (answered || !typed.trim()) return;
    const ok = sameText(typed, q.answer);
    setResult(ok ? "right" : "wrong");
    if (ok) setCorrect((c) => c + 1);
  };
  const onHint = () => {
    if (answered || hintUsed) return;
    if (q.format === "choice") {
      setHiddenOptions(shuffle(q.options.filter((o) => o !== q.answer)).slice(0, 2));
    } else if (q.format === "fixit") {
      setHintText(q.answer.slice(0, Math.max(3, Math.ceil(q.answer.length / 3))) + "...");
    } else {
      setHintSignal((n) => n + 1);
    }
    setHintUsed(true);
    setHints((h) => h + 1);
  };
  const resetQuestion = () => {
    setPicked("");
    setTyped("");
    setResult(null);
    setHintUsed(false);
    setHintText("");
    setHiddenOptions([]);
  };
  const onNext = () => {
    setIndex((i) => i + 1);
    resetQuestion();
  };
  const onAgain = () => {
    setIndex(0);
    setCorrect(0);
    setHints(0);
    resetQuestion();
    setRoundKey((k) => k + 1);
  };

  const onGradeChange = (e, g) => {
    if (!g || g === grade) return;
    setIndex(0);
    setCorrect(0);
    setHints(0);
    resetQuestion();
    if (inGame) {
      setRound([]);
      setLoading(true);
    }
    setGrade(g);
  };

  const startSkill = (code) => {
    setIndex(0);
    setCorrect(0);
    setHints(0);
    resetQuestion();
    setRound([]);
    setLoadError(false);
    setLoading(true);
    setRoundKey((k) => k + 1);
    setSkill(code);
  };

  const backToMenu = () => {
    setIndex(0);
    setCorrect(0);
    setHints(0);
    resetQuestion();
    setRound([]);
    setLoadError(false);
    setLoading(false);
    setSkill(null);
  };

  const wasRight = picked ? picked === q.answer : result === "right";

  return (
    <div>
      <AppTopBar ref={refTopBar} />
      <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{ my: { xs: 3, md: 5 }, p: { xs: 2, md: 3 }, position: "relative", overflow: "hidden", border: "3px solid #25204A", boxShadow: "0 8px 0 #25204A", borderRadius: "28px" }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ marginBottom: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <GameIcon code="zone" size={44} />
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>Game Zone</Typography>
            </Box>
            {inGame && (
              <Button size="small" variant="outlined" onClick={backToMenu}>
                Change game
              </Button>
            )}
          </Box>
          {inGame && (
            <Box display="flex" alignItems="center" gap={1} sx={{ marginBottom: 1 }}>
              <GameIcon code={skill} size={36} />
              <Typography sx={{ fontWeight: 700 }}>{skill === "mix" ? "Mix it up" : skillLabel(skill)}</Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={1} sx={{ marginBottom: 2 }}>
            <Typography variant="body2">Level:</Typography>
            <ToggleButtonGroup size="small" exclusive color="success" value={grade} onChange={onGradeChange}>
              {GAME_GRADES.map((g) => (
                <ToggleButton key={g} value={g} disabled={loading}>
                  {g}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {!inGame && (
            <>
              <Typography sx={{ marginBottom: 2 }}>Pick a game and practise. Every round has new questions.</Typography>
              {GAME_GROUPS.map((group) => (
                <Box key={group.key} sx={{ marginBottom: 2.5 }}>
                  <Typography variant="h6" sx={{ marginBottom: 1 }}>{group.title}</Typography>
                  <Box display="grid" gridTemplateColumns={{ xs: "1fr 1fr", sm: "1fr 1fr 1fr" }} gap={1.5}>
                    {GAME_SKILLS.filter((s) => s.group === group.key).map((s) => (
                      <Button
                        key={s.code}
                        onClick={() => startSkill(s.code)}
                        sx={{
                          flexDirection: "column", gap: 0.25, padding: 1.5, textTransform: "none", color: "#25204A", textAlign: "center",
                          backgroundColor: group.bg, border: "3px solid #25204A", borderRadius: "20px", boxShadow: "0 4px 0 #25204A",
                          "&:hover": { backgroundColor: group.bg, transform: "translateY(-2px)", boxShadow: "0 6px 0 #25204A" },
                          "&:active": { transform: "translateY(4px)", boxShadow: "0 0 0 #25204A" },
                        }}
                      >
                        <GameIcon code={s.code} size={64} />
                        <span style={{ fontWeight: 800, fontFamily: "'Playpen Sans', Inter, sans-serif" }}>{s.label}</span>
                        <span style={{ fontSize: "0.8rem", fontWeight: 500 }}>{s.blurb}</span>
                      </Button>
                    ))}
                  </Box>
                </Box>
              ))}
              <Button variant="contained" color="secondary" onClick={() => startSkill("mix")}>
                <GameIcon code="mix" size={26} />&nbsp;Mix it up
              </Button>
            </>
          )}

          {inGame && !loading && loadError && (
            <>
              <Alert severity="warning" sx={{ marginBottom: 2 }}>We could not load questions right now. Please try again.</Alert>
              <Box display="flex" gap={1}>
                <Button variant="contained" color="success" onClick={onAgain}>Try again</Button>
                <Button variant="outlined" onClick={backToMenu}>Change game</Button>
              </Box>
            </>
          )}

          {inGame && loading && (
            <Box display="flex" alignItems="center" gap={2} sx={{ marginY: 3 }}>
              <CircularProgress size={28} />
              <Typography>Finding new questions for you...</Typography>
            </Box>
          )}

          {inGame && !loading && !loadError && !finished && q && (
            <>
              <Typography variant="body2" sx={{ marginBottom: 0.5, fontWeight: 700 }}>
                Question {index + 1} of {round.length}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(index / round.length) * 100}
                color="success"
                sx={{ height: 12, borderRadius: 6, marginBottom: 2, border: "2px solid #25204A", backgroundColor: "#fff" }}
              />
              <Typography variant="h6" sx={{ marginBottom: 2, whiteSpace: "pre-line" }}>
                {q.prompt}
              </Typography>

              {q.format === "choice" && (
                <Box display="flex" flexDirection="column" gap={1}>
                  {q.options
                    .filter((o) => !hiddenOptions.includes(o))
                    .map((opt) => {
                      const isAnswer = picked && opt === q.answer;
                      const isWrongPick = picked && opt === picked && opt !== q.answer;
                      return (
                        <Button
                          key={opt}
                          variant={picked ? "contained" : "outlined"}
                          color={isAnswer ? "success" : isWrongPick ? "error" : "primary"}
                          disabled={!!picked && !isAnswer && !isWrongPick}
                          onClick={() => onPick(opt)}
                          sx={{ textTransform: "none", fontSize: "1.1rem" }}
                        >
                          {opt}
                        </Button>
                      );
                    })}
                </Box>
              )}

              {q.format === "fixit" && (
                <>
                  <Paper variant="outlined" sx={{ padding: 1.5, marginBottom: 2, backgroundColor: "#fff8e1" }}>
                    <Typography sx={{ fontSize: "1.1rem" }}>{q.text}</Typography>
                  </Paper>
                  <TextField
                    fullWidth
                    multiline
                    label="Type the correct version"
                    value={typed}
                    disabled={answered}
                    onChange={(e) => setTyped(e.target.value)}
                  />
                  {hintText && <Typography sx={{ marginTop: 1 }}>Hint: {hintText}</Typography>}
                </>
              )}

              {q.format === "unscramble" && (
                <UnscrambleQuestion key={index} q={q} hintSignal={hintSignal} onFinish={onFinish} disabled={answered} />
              )}
              {q.format === "match" && (
                <MatchQuestion key={index} q={q} hintSignal={hintSignal} onFinish={onFinish} disabled={answered} />
              )}

              {answered && (
                <Typography sx={{ marginTop: 2 }}>
                  {wasRight ? "Correct! 🎉" : `Not quite. The answer is "${answerText(q)}".`}
                </Typography>
              )}
              <Box sx={{ marginTop: 2 }} display="flex" gap={1}>
                {!answered && q.format === "fixit" && (
                  <Button variant="contained" color="success" onClick={onCheck} disabled={!typed.trim()}>
                    Check
                  </Button>
                )}
                {!answered && (
                  <Button onClick={onHint} disabled={hintUsed}>
                    {q.format === "choice" ? "Hint (remove 2 choices)" : q.format === "unscramble" ? "Hint (first word)" : q.format === "match" ? "Hint (show one pair)" : "Hint"}
                  </Button>
                )}
                {answered && (
                  <Button variant="contained" color="success" onClick={onNext}>
                    {index + 1 >= round.length ? "See my stars" : "Next"}
                  </Button>
                )}
              </Box>
            </>
          )}

          {finished && (
            <>
              {stars >= 2 && <Confetti key={roundKey} />}
              <Box display="flex" justifyContent="center" sx={{ marginY: 2 }}>
                {[1, 2, 3].map((n) =>
                  n <= stars ? (
                    <StarIcon key={n} className="star-pop" style={{ animationDelay: `${n * 0.18}s` }} sx={{ fontSize: 64, color: "#FFC63D", stroke: "#25204A", strokeWidth: 0.6 }} />
                  ) : (
                    <StarBorderIcon key={n} sx={{ fontSize: 56, color: "#bbb" }} />
                  )
                )}
              </Box>
              <Typography align="center" variant="h6">
                You got {correct} out of {round.length}.
              </Typography>
              <Typography align="center" variant="body2" sx={{ marginBottom: 2 }}>
                Hints used: {hints}. Play again for brand new questions!
              </Typography>
              <Box display="flex" justifyContent="center" gap={1}>
                <Button variant="contained" color="success" onClick={onAgain}>
                  Play again
                </Button>
                <Button variant="outlined" onClick={backToMenu}>
                  Change game
                </Button>
                <Button variant="outlined" onClick={() => navigate("/home")}>
                  Back home
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
}
